import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Users, DollarSign, TrendingUp, AlertCircle, 
  Search, Home, Settings, LogOut, CreditCard,
  CheckCircle, XCircle, Clock, MessageSquare, Send, Briefcase, UserCheck, FileText, Mail, BarChart3, Sparkles, Shield, Trash2, Globe, Trophy, LineChart
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SMSNotification from '../components/SMSNotification';
import SMSMarketing from '../components/SMSMarketing';
import CreateLeadForm from '../components/CreateLeadForm';
import RealtorManagement from '../components/RealtorManagement';
import LeadManagement from '../components/LeadManagement';
import DripCampaignManager from '../components/DripCampaignManager';
import DripCampaignTemplates from '../components/DripCampaignTemplates';
import AffiliateOutreach from '../components/AffiliateOutreach';
import AffiliateAnalytics from '../components/AffiliateAnalytics';
import SellerCheckInMonitor from '../components/SellerCheckInMonitor';
import PromotionManager from '../components/PromotionManager';
import FranchiseInquiryManager from '../components/FranchiseInquiryManager';
import PlatformSettingsManager from '../components/PlatformSettingsManager';
import LicenseVerificationManager from '../components/LicenseVerificationManager';
import ContestManagement from '../components/ContestManagement';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import CitationManager from '../components/CitationManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// SEO Tools Panel component for inline display
const SEOToolsPanel = () => {
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const submitToIndexNow = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/admin/indexnow/submit`,
        { submit_all: true },
        { withCredentials: true }
      );

      if (response.data.success) {
        setLastResult(response.data);
        toast.success(`✅ ${response.data.message}`);
      } else {
        toast.error(`❌ ${response.data.message}`);
      }
    } catch (error) {
      console.error('IndexNow error:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to submit to IndexNow';
      if (errorMsg.includes('SiteVerification') || errorMsg.includes('keylocation')) {
        toast.error('IndexNow requires deployment to production. The verification file must be accessible at its-sold.com');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* IndexNow Card */}
      <Card className="p-6">
        <div className="flex items-start space-x-4">
          <div className="bg-blue-100 rounded-lg p-3">
            <Globe className="w-8 h-8 text-blue-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900 mb-2">IndexNow - Instant Bing Indexing</h2>
            <p className="text-gray-600 mb-4">
              Submit your site URLs to Bing for instant indexing. This bypasses the normal crawling process and gets your pages indexed within minutes.
            </p>
            
            <div className="bg-amber-50 border-l-4 border-amber-500 p-3 mb-4 text-sm">
              <strong>⚠️ Note:</strong> IndexNow only works from production (its-sold.com). Deploy first, then submit.
            </div>
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
              <h3 className="font-semibold text-blue-900 mb-2">What gets submitted ({lastResult?.urls_submitted || '813+'} URLs):</h3>
              <ul className="text-sm text-blue-800 space-y-1 grid grid-cols-2 gap-x-4">
                <li>✅ Homepage & static pages</li>
                <li>✅ All 100+ DFW city pages</li>
                <li>✅ All 12 county pages</li>
                <li>✅ All active listings</li>
                <li>✅ All blog posts</li>
                <li>✅ Contest & leaderboard pages</li>
              </ul>
            </div>

            <div className="flex items-center gap-4">
              <Button
                onClick={submitToIndexNow}
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {submitting ? (
                  <>
                    <Search className="w-4 h-4 mr-2 animate-spin" />
                    Submitting to IndexNow...
                  </>
                ) : (
                  <>
                    <Globe className="w-4 h-4 mr-2" />
                    Submit All URLs to IndexNow
                  </>
                )}
              </Button>
              
              {lastResult && (
                <span className="text-sm text-green-600 font-medium">
                  ✅ Submitted {lastResult.urls_submitted} URLs
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Sitemap Card */}
      <Card className="p-6">
        <div className="flex items-start space-x-4">
          <div className="bg-green-100 rounded-lg p-3">
            <FileText className="w-8 h-8 text-green-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Sitemap (813+ URLs)</h2>
            <p className="text-gray-600 mb-4">
              Your sitemap is automatically generated with all public pages. No auth pages included.
            </p>
            
            <div className="bg-gray-100 border border-gray-300 rounded p-3 mb-4 font-mono text-sm">
              https://its-sold.com/api/sitemap.xml
            </div>

            <div className="flex flex-wrap gap-2">
              <a href="https://its-sold.com/api/sitemap.xml" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm">
                  <FileText className="w-4 h-4 mr-2" />
                  View Sitemap
                </Button>
              </a>
              <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm">
                  Google Search Console
                </Button>
              </a>
              <a href="https://www.bing.com/webmasters" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm">
                  Bing Webmaster Tools
                </Button>
              </a>
            </div>
          </div>
        </div>
      </Card>

      {/* Tips Card */}
      <Card className="p-6 bg-amber-50 border-amber-200">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-6 h-6 text-amber-600 mt-1" />
          <div>
            <h3 className="font-semibold text-amber-900 mb-2">SEO Tips</h3>
            <ul className="text-sm text-amber-800 space-y-1">
              <li>• Use IndexNow after adding new listings or blog posts</li>
              <li>• Submit to IndexNow max once per day (don&apos;t spam)</li>
              <li>• Google will re-crawl within 1-2 weeks after sitemap updates</li>
              <li>• All city/county pages have unique canonical tags</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

const AdminDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [admin, setAdmin] = useState(user);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [cancellationFeedback, setCancellationFeedback] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [realtorApplications, setRealtorApplications] = useState([]);
  const [approvedRealtors, setApprovedRealtors] = useState([]);
  const [leads, setLeads] = useState([]);
  const [showCreateLeadDialog, setShowCreateLeadDialog] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const isMountedRef = useRef(true);
  const isLoggingOutRef = useRef(false);
  
  // Ensure isMountedRef is set on mount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Trust App.js routing protection - user prop is guaranteed to be admin
    if (user && (user.is_admin || user.is_super_admin)) {
      setAdmin(user);
      setLoading(false); // Stop initial loading state
      fetchData();
    }
  }, [user]);

  // Fetch data when active tab changes
  useEffect(() => {
    if (admin) {
      fetchData();
    }
  }, [activeTab]);

  const fetchData = async () => {
    // Skip if unmounted or logging out
    if (!isMountedRef.current || isLoggingOutRef.current) {
      console.log('fetchData skipped - unmounted or logging out');
      return;
    }
    
    setLoading(true);
    try {
      if (activeTab === 'overview') {
        const response = await axios.get(`${API}/admin/stats`, { withCredentials: true });
        console.log('Stats response:', response.data);
        console.log('isMountedRef.current:', isMountedRef.current);
        setStats(response.data);
        console.log('Stats set successfully');
      } else if (activeTab === 'users') {
        const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
        console.log('Users response:', response.data);
        if (isMountedRef.current) setUsers(response.data);
      } else if (activeTab === 'subscriptions') {
        const response = await axios.get(`${API}/admin/subscriptions`, { withCredentials: true });
        console.log('Subscriptions response:', response.data);
        if (isMountedRef.current) setSubscriptions(response.data);
      } else if (activeTab === 'feedback') {
        const response = await axios.get(`${API}/admin/cancellation-feedback`, { withCredentials: true });
        console.log('Feedback response:', response.data);
        if (isMountedRef.current) setCancellationFeedback(response.data);
      } else if (activeTab === 'realtors') {
        const [applicationsRes, approvedRes] = await Promise.all([
          axios.get(`${API}/admin/realtor-applications`, { withCredentials: true }),
          axios.get(`${API}/admin/approved-realtors`, { withCredentials: true })
        ]);
        if (isMountedRef.current) {
          setRealtorApplications(applicationsRes.data.applications || []);
          setApprovedRealtors(approvedRes.data.realtors || []);
        }
      } else if (activeTab === 'leads') {
        const response = await axios.get(`${API}/admin/leads`, { withCredentials: true });
        if (isMountedRef.current) setLeads(response.data.leads || []);
      }
    } catch (error) {
      console.error('Fetch data error:', error);
      // Only show error if not logging out and component is mounted
      if (isMountedRef.current && !isLoggingOutRef.current) {
        toast.error('Failed to fetch data: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleLogout = async () => {
    // Set flags to prevent further API calls
    setIsLoggingOut(true);
    isLoggingOutRef.current = true;
    
    // Close any open dialogs immediately
    setDeleteDialogOpen(false);
    setUserToDelete(null);
    setDeleteConfirmed(false);
    
    // Clear admin state to prevent useEffect triggers
    setAdmin(null);
    
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      // Clear user state in App.js to prevent re-mounting
      setUser(null);
      // Navigate immediately
      navigate('/login');
    } catch (error) {
      // Only handle error if component is still mounted
      if (isMountedRef.current) {
        setIsLoggingOut(false);
        isLoggingOutRef.current = false;
        toast.error('Logout failed');
      }
    }
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setDeleteConfirmed(false);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    // Prevent deletion during logout or if unmounted
    if (isLoggingOutRef.current || !isMountedRef.current) {
      return;
    }
    
    if (!deleteConfirmed) {
      toast.error('Please check the confirmation box');
      return;
    }

    try {
      const response = await axios.delete(`${API}/admin/users/${userToDelete.id}`, { 
        withCredentials: true 
      });
      
      toast.success(
        `User deleted: ${userToDelete.email}\n` +
        `Deleted ${response.data.summary.deleted_items.listings} listings, ` +
        `${response.data.summary.deleted_items.inquiries} inquiries`
      );
      
      // Close dialog and refresh
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setDeleteConfirmed(false);
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Only super admins can delete users');
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Cannot delete this user');
      } else {
        toast.error('Failed to delete user');
      }
    }
  };

  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selectable users (exclude super admins and current admin)
  const selectableUsers = filteredUsers.filter(u => !u.is_super_admin && u.id !== admin?.id);

  const handleSelectAllUsers = (e) => {
    if (e.target.checked) {
      setSelectedUsers(selectableUsers.map(u => u.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const handleBulkDeleteUsers = async () => {
    if (selectedUsers.length === 0) return;
    
    setIsBulkDeleting(true);
    try {
      const response = await axios.post(
        `${API}/admin/users/bulk-delete`,
        { ids: selectedUsers },
        { withCredentials: true }
      );
      
      toast.success(`Successfully deleted ${response.data.deleted_count} user(s)`);
      setSelectedUsers([]);
      setBulkDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete users');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  if (loading && !admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
                <Settings className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-xs text-gray-500">Its-Sold.com Management</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button onClick={() => navigate('/')} variant="ghost" size="sm">
                <Home className="w-4 h-4 mr-2" />
                Main Site
              </Button>
              <Button onClick={() => navigate('/admin/settings')} variant="ghost" size="sm">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
              <span className="text-sm text-gray-600">Admin: {admin?.name}</span>
              <Button onClick={handleLogout} variant="ghost" size="sm">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Tabs - Scrollable with Navigation Arrows */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center">
            {/* Left scroll button */}
            <button
              onClick={() => {
                const container = document.getElementById('admin-tabs-container');
                if (container) container.scrollLeft -= 200;
              }}
              className="absolute left-0 z-10 bg-gradient-to-r from-white via-white to-transparent pl-1 pr-4 py-3 text-gray-500 hover:text-orange-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            {/* Tabs container */}
            <div 
              id="admin-tabs-container"
              className="flex space-x-1 overflow-x-auto scroll-smooth mx-8 py-1"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
            >
              {[
                { id: 'overview', label: 'Overview', icon: TrendingUp },
                { id: 'analytics', label: '📊 Analytics', icon: LineChart },
                { id: 'users', label: 'Users', icon: Users },
                { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
                { id: 'realtors', label: 'Realtors', icon: UserCheck },
                { id: 'leads', label: 'Leads', icon: Briefcase },
                { id: 'promotions', label: 'Promos', icon: Sparkles },
                { id: 'franchise', label: 'Franchise', icon: TrendingUp },
                { id: 'seller-checkins', label: 'Check-Ins', icon: CheckCircle },
                { id: 'drip-campaigns', label: 'Drip', icon: Send },
                { id: 'templates', label: 'Templates', icon: FileText },
                { id: 'license-verifications', label: 'Licenses', icon: Shield },
                { id: 'affiliate-outreach', label: 'Affiliates', icon: Mail },
                { id: 'affiliate-analytics', label: 'Aff. Analytics', icon: BarChart3 },
                { id: 'citations', label: '🔗 Citations', icon: Globe },
                { id: 'seo-tools', label: '🔍 SEO', icon: Globe },
                { id: 'platform-settings', label: 'Settings', icon: Settings },
                { id: 'sms-marketing', label: '📱 SMS Marketing', icon: MessageSquare },
                { id: 'feedback', label: 'Feedback', icon: AlertCircle }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-1.5 py-2.5 px-3 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 rounded-t-lg ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600 bg-orange-50 font-semibold'
                      : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="text-sm">{tab.label}</span>
                </button>
              ))}
            </div>
            
            {/* Right scroll button */}
            <button
              onClick={() => {
                const container = document.getElementById('admin-tabs-container');
                if (container) container.scrollLeft += 200;
              }}
              className="absolute right-0 z-10 bg-gradient-to-l from-white via-white to-transparent pr-1 pl-4 py-3 text-gray-500 hover:text-orange-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading data...</p>
          </div>
        )}
        
        {!loading && activeTab === 'overview' && !stats && (
          <div className="text-center py-12">
            <p className="text-gray-600">No stats data available</p>
          </div>
        )}
        
        {!loading && activeTab === 'overview' && stats && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Users</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.total_users}</p>
                    <p className="text-xs text-green-600 mt-1">+{stats.new_users_this_month} this month</p>
                  </div>
                  <Users className="w-12 h-12 text-blue-500" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Active Subscriptions</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.active_subscriptions}</p>
                    <p className="text-xs text-gray-500 mt-1">{stats.total_subscriptions} total</p>
                  </div>
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Monthly Revenue</p>
                    <p className="text-3xl font-bold text-gray-900">${stats.monthly_revenue}</p>
                    <p className="text-xs text-green-600 mt-1">+{stats.revenue_growth}% vs last month</p>
                  </div>
                  <DollarSign className="w-12 h-12 text-green-500" />
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Failed Payments</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.failed_payments}</p>
                    <p className="text-xs text-red-600 mt-1">{stats.suspended_accounts} suspended</p>
                  </div>
                  <AlertCircle className="w-12 h-12 text-red-500" />
                </div>
              </Card>
            </div>

            {/* Charts/Additional Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Subscription Breakdown</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Free Tier</span>
                    <span className="font-semibold">{stats.free_tier_count} ({stats.free_tier_percent}%)</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Featured ($49/mo)</span>
                    <span className="font-semibold text-orange-600">{stats.featured_count} ({stats.featured_percent}%)</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Premium ($99/mo)</span>
                    <span className="font-semibold text-purple-600">{stats.premium_count} ({stats.premium_percent}%)</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">New Subscription</p>
                      <p className="text-xs text-gray-500">{stats.last_subscription_time}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Users className="w-5 h-5 text-blue-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">New User Registration</p>
                      <p className="text-xs text-gray-500">{stats.last_user_time}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Subscription Cancelled</p>
                      <p className="text-xs text-gray-500">{stats.last_cancellation_time}</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <AnalyticsDashboard />
        )}

        {/* Contest tab removed - re-enable when site has traffic
        {activeTab === 'contest' && (
          <ContestManagement />
        )}
        */}

        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search and Bulk Actions */}
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              {admin?.is_super_admin && selectedUsers.length > 0 && (
                <Button
                  variant="destructive"
                  onClick={() => setBulkDeleteDialogOpen(true)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected ({selectedUsers.length})
                </Button>
              )}
              <Button onClick={fetchData}>
                Refresh
              </Button>
            </div>

            {/* Users Table */}
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      {admin?.is_super_admin && (
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedUsers.length === selectableUsers.length && selectableUsers.length > 0}
                            onChange={handleSelectAllUsers}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                          />
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscription</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Listings</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      {admin?.is_super_admin && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className={`hover:bg-gray-50 ${selectedUsers.includes(user.id) ? 'bg-orange-50' : ''}`}>
                        {admin?.is_super_admin && (
                          <td className="px-4 py-4">
                            {!user.is_super_admin && user.id !== admin.id ? (
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={() => handleSelectUser(user.id)}
                                className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                              />
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{user.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            user.subscription_tier === 'premium' 
                              ? 'bg-purple-100 text-purple-800'
                              : user.subscription_tier === 'featured'
                              ? 'bg-orange-100 text-orange-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.subscription_tier || 'Free'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.listing_count}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.suspended ? (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                              Suspended
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              Active
                            </span>
                          )}
                        </td>
                        {admin?.is_super_admin && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleDeleteUser(user)}
                              disabled={user.is_super_admin || user.id === admin.id}
                              className={`text-sm font-medium ${
                                user.is_super_admin || user.id === admin.id
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : 'text-red-600 hover:text-red-900'
                              }`}
                            >
                              {user.is_super_admin ? 'Protected' : user.id === admin.id ? 'You' : 'Delete'}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'subscriptions' && (
          <div className="space-y-6">
            <Card className="overflow-hidden">
              {subscriptions.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg mb-2">No Active Subscriptions</p>
                  <p className="text-gray-400 text-sm">All users are currently on the free tier</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Payment</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {subscriptions.map((sub) => (
                      <tr key={sub.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{sub.user_name}</div>
                          <div className="text-xs text-gray-500">{sub.user_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            sub.tier === 'premium' 
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {sub.tier}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            sub.status === 'active' 
                              ? 'bg-green-100 text-green-800'
                              : sub.status === 'past_due'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {sub.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${sub.tier === 'premium' ? '99' : '49'}/mo
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(sub.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : 'N/A'}
                        </td>
                      </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}

        {activeTab === 'realtors' && (
          <div className="space-y-6">
            {/* Pending Applications */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Pending Applications</h3>
              {realtorApplications.filter(app => app.status === 'pending').length === 0 ? (
                <p className="text-gray-500 text-center py-8">No pending applications</p>
              ) : (
                <div className="space-y-4">
                  {realtorApplications.filter(app => app.status === 'pending').map(application => (
                    <div key={application.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900">{application.name}</h4>
                          <p className="text-sm text-gray-600">{application.email}</p>
                          <p className="text-sm text-gray-600">{application.phone}</p>
                        </div>
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
                          Pending
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                        <div>
                          <span className="text-gray-500">License:</span>
                          <span className="ml-2 font-medium">{application.license_number}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Experience:</span>
                          <span className="ml-2 font-medium">{application.years_experience} years</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Brokerage:</span>
                          <span className="ml-2 font-medium">{application.brokerage}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Areas:</span>
                          <span className="ml-2 font-medium">{application.areas_served.join(', ')}</span>
                        </div>
                      </div>
                      {application.specialties && application.specialties.length > 0 && (
                        <div className="mb-3">
                          <span className="text-sm text-gray-500">Specialties: </span>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {application.specialties.map(specialty => (
                              <span key={specialty} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                                {specialty}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {application.bio && (
                        <p className="text-sm text-gray-600 mb-3 italic">&ldquo;{application.bio}&rdquo;</p>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            try {
                              await axios.post(
                                `${API}/admin/realtor-applications/${application.id}/approve`,
                                {},
                                { withCredentials: true }
                              );
                              toast.success('Application approved!');
                              fetchData();
                            } catch (error) {
                              toast.error('Failed to approve application');
                            }
                          }}
                          className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 flex items-center justify-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve
                        </button>
                        <button
                          onClick={async () => {
                            const reason = prompt('Rejection reason (optional):');
                            try {
                              await axios.post(
                                `${API}/admin/realtor-applications/${application.id}/reject?reason=${encodeURIComponent(reason || '')}`,
                                {},
                                { withCredentials: true }
                              );
                              toast.success('Application rejected');
                              fetchData();
                            } catch (error) {
                              toast.error('Failed to reject application');
                            }
                          }}
                          className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 flex items-center justify-center gap-2"
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Approved Realtors with Management Actions */}
            <RealtorManagement approvedRealtors={approvedRealtors} fetchData={fetchData} />
          </div>
        )}

        {activeTab === 'leads' && (
          <LeadManagement 
            leads={leads} 
            setShowCreateLeadDialog={setShowCreateLeadDialog} 
            fetchData={fetchData} 
          />
        )}

        {activeTab === 'promotions' && (
          <PromotionManager />
        )}

        {activeTab === 'franchise' && (
          <FranchiseInquiryManager />
        )}

        {activeTab === 'seller-checkins' && (
          <SellerCheckInMonitor />
        )}

        {activeTab === 'drip-campaigns' && (
          <DripCampaignManager />
        )}

        {activeTab === 'templates' && (
          <DripCampaignTemplates />
        )}

        {activeTab === 'affiliate-outreach' && (
          <AffiliateOutreach />
        )}

        {activeTab === 'affiliate-analytics' && (
          <AffiliateAnalytics />
        )}

        {activeTab === 'license-verifications' && (
          <LicenseVerificationManager />
        )}

        {activeTab === 'platform-settings' && (
          <PlatformSettingsManager />
        )}

        {activeTab === 'sms-marketing' && (
          <SMSMarketing />
        )}

        {activeTab === 'seo-tools' && (
          <SEOToolsPanel />
        )}

        {activeTab === 'citations' && (
          <CitationManager />
        )}

        {activeTab === 'feedback' && (
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Cancellation Feedback</h3>
              <div className="space-y-4">
                {cancellationFeedback.map((feedback) => (
                  <div key={feedback.id} className="border-l-4 border-orange-500 bg-orange-50 p-4 rounded">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium text-gray-900">{feedback.user_name}</p>
                        <p className="text-sm text-gray-500">{feedback.user_email}</p>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(feedback.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Reason:</strong> {feedback.reason}
                    </p>
                    {feedback.comments && (
                      <p className="text-sm text-gray-600 italic">
                        &ldquo;{feedback.comments}&rdquo;
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Discount offered: {feedback.discount_offered ? 'Yes' : 'No'} | 
                      Accepted: {feedback.discount_accepted ? 'Yes' : 'No'}
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) {
          // Cleanup when dialog closes
          setUserToDelete(null);
          setDeleteConfirmed(false);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              Delete User - Permanent Action
            </DialogTitle>
          </DialogHeader>
          
          {userToDelete && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-semibold text-gray-900 mb-2">
                  User: {userToDelete.email}
                </p>
                <p className="text-sm text-gray-700 mb-3">
                  This will permanently delete:
                </p>
                <ul className="text-sm text-gray-700 space-y-1 ml-4">
                  <li>• User account</li>
                  <li>• {userToDelete.listing_count} listing(s)</li>
                  <li>• All inquiries</li>
                  <li>• All notifications</li>
                  <li>• Subscription data</li>
                </ul>
                <p className="text-sm font-bold text-red-600 mt-3">
                  ⚠️ This action CANNOT be undone!
                </p>
              </div>

              {/* Confirmation Checkbox */}
              <div className="flex items-start space-x-3 bg-gray-50 p-3 rounded-lg border border-gray-200">
                <input
                  type="checkbox"
                  id="delete-confirm"
                  checked={deleteConfirmed}
                  onChange={(e) => setDeleteConfirmed(e.target.checked)}
                  className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label htmlFor="delete-confirm" className="text-sm text-gray-700 cursor-pointer">
                  I understand this will permanently delete <strong>{userToDelete.email}</strong> and all their data. This cannot be undone.
                </label>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setUserToDelete(null);
                setDeleteConfirmed(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={!deleteConfirmed}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Lead Dialog */}
      {showCreateLeadDialog && (
        <CreateLeadForm
          onClose={() => setShowCreateLeadDialog(false)}
          onSuccess={() => fetchData()}
        />
      )}

      {/* Bulk Delete Users Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              Delete {selectedUsers.length} User(s)
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-3">
                You are about to permanently delete <strong>{selectedUsers.length}</strong> user(s) and all their associated data:
              </p>
              <ul className="text-sm text-gray-700 space-y-1 ml-4">
                <li>• All listings</li>
                <li>• All inquiries</li>
                <li>• All subscriptions</li>
                <li>• All notifications</li>
              </ul>
              <p className="text-sm font-bold text-red-600 mt-3">
                ⚠️ This action CANNOT be undone!
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkDeleteDialogOpen(false)}
              disabled={isBulkDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDeleteUsers}
              disabled={isBulkDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isBulkDeleting ? 'Deleting...' : `Delete ${selectedUsers.length} User(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
